import React, { useEffect, useRef } from 'react';
import { 
  db, 
  handleFirestoreError, 
  OperationType 
} from '../firebase';
import { collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  serverTimestamp } from '../firebase';


interface FinancialNotificationsCheckerProps {
  user: any;
  orgId: string;
}

export default function FinancialNotificationsChecker({ user, orgId }: FinancialNotificationsCheckerProps) {
  const lastRunKey = useRef<string | null>(null);

  useEffect(() => {
    if (!user || !orgId) {
      lastRunKey.current = null;
      return;
    }

    const runKey = `${user.uid || user.id || ''}:${orgId}`;
    if (lastRunKey.current === runKey) return;

    const checkNotifications = async () => {
      lastRunKey.current = runKey;
      const today = new Date().toISOString().split('T')[0];
      
      try {
        const localToken = typeof window === 'undefined' ? null : localStorage.getItem('app_auth_token');
        const recipientUserId = localToken ? user.id || user.uid : user.uid || user.id;
        const documentsRef = collection(db, 'documents');
        const nextMonth = new Date();
        nextMonth.setDate(nextMonth.getDate() + 30);
        const nextMonthStr = nextMonth.toISOString().split('T')[0];
        
        // Use a generic query to find pending items that might need attention
        // We'll filter them client-side for precise date logic to avoid complex composite indices if possible,
        // but we'll try to refine the query window.
        
        const q = query(
          documentsRef,
          where('orgId', '==', orgId),
          where('status', 'in', ['Draft', 'Sent', 'Awaiting Payment', 'Partially Paid', 'Awaiting Approval', 'Approved', 'Billed'])
        );
        const documents = localToken
          ? await fetch(`/api/organizations/${orgId}/documents?types=invoice,quote,bill,purchase_order`, {
              headers: { Authorization: `Bearer ${localToken}` }
            }).then(async response => {
              if (!response.ok) throw new Error('Could not load local financial documents');
              const records = await response.json();
              return (Array.isArray(records) ? records : []).map(document => ({ id: document.id, data: document }));
            })
          : (await getDocs(q)).docs.map(document => ({ id: document.id, data: document.data() }));
        for (const document of documents) {
          const data = document.data;
          const docType = data.type;
          const contactName = data.contactName || data.contact_name || data.customerName || 'this customer';
          const currency = data.currency || data.currencyCode || 'EGP';
          const totalAmount = data.totalAmount ?? data.total_amount ?? 0;
          const dueDate = typeof (data.dueDate || data.deliveryDate) === 'string'
            ? (data.dueDate || data.deliveryDate).slice(0, 10)
            : data.dueDate || data.deliveryDate;
          
          if (!dueDate) continue;

          let title = '';
          let message = '';
          let notifyType = '';

          const isOverdue = dueDate < today;
          const isDueToday = dueDate === today;
          const isUpcoming = dueDate > today && dueDate <= nextMonthStr;

          if (isOverdue || isDueToday || isUpcoming) {
            if (docType === 'invoice') {
              notifyType = isOverdue ? 'invoice_overdue' : (isDueToday ? 'invoice_due' : 'invoice_upcoming');
              title = isOverdue ? 'Invoice Overdue' : (isDueToday ? 'Invoice Due Today' : 'Upcoming Invoice Payment');
              message = `Invoice ${data.number} for ${contactName} ${isOverdue ? 'is past due' : (isDueToday ? 'is due today' : 'is due soon')}. Total: ${currency} ${totalAmount}`;
            } else if (docType === 'quote') {
              notifyType = isOverdue ? 'quote_expired' : (isDueToday ? 'quote_expiring' : 'quote_upcoming_expiry');
              title = isOverdue ? 'Quote Expired' : (isDueToday ? 'Quote Expiring Today' : 'Upcoming Quote Expiry');
              message = `Quote ${data.number} for ${contactName} ${isOverdue ? 'has expired' : (isDueToday ? 'expires today' : 'expires soon')}.`;
            } else if (docType === 'bill') {
              notifyType = isOverdue ? 'bill_overdue' : (isDueToday ? 'bill_due' : 'bill_upcoming');
              title = isOverdue ? 'Bill Overdue' : (isDueToday ? 'Bill Due Today' : 'Upcoming Bill Payment');
              message = `Bill ${data.number} from ${contactName} ${isOverdue ? 'is overdue' : (isDueToday ? 'is due today' : 'is due soon')}. Total: ${currency} ${totalAmount}`;
            } else if (docType === 'purchase_order') {
              const poDate = data.deliveryDate || data.date;
              if (poDate) {
                 const poOverdue = poDate < today;
                 const poToday = poDate === today;
                 const poUpcoming = poDate > today && poDate <= nextMonthStr;
                 
                 if (poOverdue || poToday || poUpcoming) {
                   notifyType = poOverdue ? 'po_overdue' : (poToday ? 'po_delivery' : 'po_upcoming');
                   title = poOverdue ? 'PO Overdue' : (poToday ? 'PO Delivery Today' : 'Upcoming PO Delivery');
                   message = `Purchase Order ${data.number} from ${contactName} ${poOverdue ? 'is overdue' : (poToday ? 'is scheduled for delivery today' : 'is due for delivery soon')}.`;
                 }
              }
            }

            if (title && message && notifyType) {
              await createNotificationIfNotExists(
                recipientUserId,
                orgId,
                title,
                message,
                notifyType,
                document.id,
                today // Use today as eventDate to ensure we only notify once per day about this item
              );
            }
          }
        }
      } catch (error) {
        // Silently handle offline/background check failures
      }
    };

    checkNotifications();
  }, [user, orgId]);

  return null;
}

async function createNotificationIfNotExists(
  userId: string,
  orgId: string,
  title: string,
  message: string,
  type: string,
  relatedId: string,
  eventDate: string
) {
  try {
    const localToken = typeof window === 'undefined' ? null : localStorage.getItem('app_auth_token');
    if (localToken) {
      const response = await fetch(`/api/organizations/${orgId}/notifications`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ recipientUserId: userId, title, message, type, relatedId, eventDate })
      });
      if (response.ok) return;
    }

    // Direct addDoc fallback
    const notificationsRef = collection(db, 'notifications');
    await addDoc(notificationsRef, {
      userId,
      orgId,
      title,
      message,
      type,
      relatedId,
      eventDate,
      read: false,
      createdAt: serverTimestamp()
    });
  } catch (err) {
    // Graceful offline fallback in localStorage
    try {
      const key = `accountix_notifs_${orgId}`;
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const isDuplicate = existing.some((n: any) => n.relatedId === relatedId && n.type === type && n.eventDate === eventDate);
      if (!isDuplicate) {
        existing.unshift({
          id: `local_notif_${Date.now()}`,
          userId,
          orgId,
          title,
          message,
          type,
          relatedId,
          eventDate,
          read: false,
          createdAt: new Date().toISOString()
        });
        localStorage.setItem(key, JSON.stringify(existing.slice(0, 50)));
      }
    } catch {
      // Ignore local storage errors
    }
  }
}
